// The species map's data: a collection site x species pivot joined to the
// collection sites' locations. Pure functions, no React or Leaflet.
//
// Each site with a location becomes a pie of its species, colored and folded
// exactly as the bar chart does it (chartSeries): the organization's top eight
// species keep their colors, the rest share "Other species", and the
// non-species results keep their neutrals. A pie's area follows the site's
// total count, so a site with four times the specimens covers four times the
// map, within limits that keep small sites visible and large ones from
// burying their neighbors.
import { cellValue, entryLabel } from "../PivotTable/pivotModel";
import { chartSeries } from "../Charts/chartData";
import { OTHER_COLOR } from "../../../../../utils/chartPalette";

export const MIN_RADIUS = 12;
export const MAX_RADIUS = 48;

const FONT = 'Roboto, system-ui, -apple-system, "Segoe UI", sans-serif';
const NUMBER = new Intl.NumberFormat();

/** A pie's radius for `total` when the largest site has `max`: area follows the count. */
export const pieRadius = (total, max) => {
  if (!max || !total) return MIN_RADIUS;
  return Math.min(
    MAX_RADIUS,
    Math.max(MIN_RADIUS, MAX_RADIUS * Math.sqrt(total / max)),
  );
};

const location = (site) => {
  const lat = Number(site?.gpsLocation?.latitude);
  const lng = Number(site?.gpsLocation?.longitude);
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
    ? [lat, lng]
    : null;
};

/**
 * @param model     a collectionSite x species pivot model
 * @param trapSites the organization's collection sites by id (state.trapSites)
 * @param scale     the species color scale (useSpeciesColors)
 * @returns {
 *   pies:     [{key, name, county, position: [lat, lng], total, radius,
 *              slices: [{key, label, color, count}], species: [{label, color, count, column}], row}],
 *   unplaced: [{key, name, total, reason, row}] - sites with no location, and
 *             the specimens with no collection site, in the pivot's order
 *   legend:   [{key, label, color, total, members?}] - every series, in order, with
 *             its count over the whole report
 * }
 */
export const mapModel = (model, trapSites, scale) => {
  const { groups, series } = chartSeries(model, scale);
  const colorOf = new Map(series.map((s) => [s.key, s.color]));
  const folded = new Set(series.flatMap((s) => s.members ?? []));

  // every species at a site, unfolded, for its popup
  const speciesAt = (r) =>
    model.columns
      .map((column, c) => ({ column, count: cellValue(model, r, c) }))
      .filter(({ count }) => count > 0)
      .map(({ column, count }) => {
        const label = entryLabel(column);
        return {
          label,
          count,
          column,
          color: folded.has(label)
            ? OTHER_COLOR
            : (colorOf.get(label) ?? OTHER_COLOR),
        };
      });

  const placed = [];
  const unplaced = [];
  groups.forEach((group) => {
    const row = model.rows[group.r];
    const siteKey = group.key?.[0];
    const site = trapSites?.[siteKey];
    const position = location(site);
    if (!position) {
      unplaced.push({
        key: siteKey,
        name: group.name,
        total: group.total,
        reason: "No location",
        row,
      });
      return;
    }
    placed.push({
      key: siteKey,
      name: group.name,
      county: site.county || null,
      position,
      total: group.total,
      slices: series
        .filter((s) => group.values[s.key] > 0)
        .map((s) => ({
          key: s.key,
          label: s.label,
          color: s.color,
          count: group.values[s.key],
        })),
      species: speciesAt(group.r),
      row,
    });
  });
  // the catch-all row: specimens from trays with no collection site
  model.rows.forEach((row, r) => {
    if (row.category[0] !== "none") return;
    const total = model.rowTotals[r] ?? 0;
    if (total > 0)
      unplaced.push({
        key: "",
        name: entryLabel(row),
        total,
        reason: "No collection site",
        row,
      });
  });

  const max = Math.max(0, ...placed.map((p) => p.total));
  const pies = placed.map((p) => ({ ...p, radius: pieRadius(p.total, max) }));

  // each series' count over the whole report, on the map or not
  const columnTotal = new Map(
    model.columns.map((column, c) => [
      entryLabel(column),
      model.columnTotals[c] ?? 0,
    ]),
  );
  const legend = series
    .map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      members: s.members,
      total: (s.members ?? [s.key]).reduce(
        (sum, label) => sum + (columnTotal.get(label) ?? 0),
        0,
      ),
    }))
    .filter((s) => s.total > 0);

  return { pies, unplaced, legend };
};

const arc = (cx, cy, r, from, to) => {
  const x = (a) => cx + r * Math.cos(a);
  const y = (a) => cy + r * Math.sin(a);
  return {
    x0: x(from),
    y0: y(from),
    x1: x(to),
    y1: y(to),
    large: to - from > Math.PI ? 1 : 0,
  };
};

// one donut slice between two angles (radians, clockwise from 12 o'clock)
const slicePath = (c, outer, inner, from, to) => {
  const o = arc(c, c, outer, from, to);
  const i = arc(c, c, inner, to, from);
  return [
    `M${o.x0.toFixed(2)},${o.y0.toFixed(2)}`,
    `A${outer},${outer} 0 ${o.large} 1 ${o.x1.toFixed(2)},${o.y1.toFixed(2)}`,
    `L${i.x0.toFixed(2)},${i.y0.toFixed(2)}`,
    `A${inner},${inner} 0 ${o.large} 0 ${i.x1.toFixed(2)},${i.y1.toFixed(2)}`,
    "Z",
  ].join(" ");
};

const escapeXml = (text) =>
  String(text).replace(
    /[<>&"']/g,
    (ch) =>
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[ch],
  );

/**
 * A site's pie as a self-contained SVG string, for the marker and the image
 * download alike: a donut of its species with a surface-colored gap between
 * slices and a ring that keeps it apart from the map and its neighbors, the
 * total in the hole when it fits.
 */
export const pieSvg = ({ name, slices, total, radius }) => {
  const ring = 2;
  const size = Math.ceil((radius + ring) * 2);
  const c = size / 2;
  const inner = radius * 0.52;
  const start = -Math.PI / 2;
  let angle = start;
  const shapes = slices.map((s) => {
    const sweep = (s.count / total) * Math.PI * 2;
    const from = angle;
    angle += sweep;
    // a whole ring cannot be one arc
    if (slices.length === 1) {
      return `<circle cx="${c}" cy="${c}" r="${(radius + inner) / 2}" fill="none" stroke="${s.color}" stroke-width="${radius - inner}"/>`;
    }
    return `<path d="${slicePath(c, radius, inner, from, angle)}" fill="${s.color}" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>`;
  });

  const label = NUMBER.format(total);
  const fontSize = Math.round(Math.max(10, Math.min(14, inner * 0.62)));
  const fits = radius >= 18 && label.length * fontSize * 0.6 <= inner * 1.8;
  const text = fits
    ? `<text x="${c}" y="${c}" text-anchor="middle" dominant-baseline="central" font-family='${FONT}' font-size="${fontSize}" font-weight="500" fill="#0b0b0b">${label}</text>`
    : "";

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="${escapeXml(`${name}: ${label} specimens`)}">`,
    `<circle cx="${c}" cy="${c}" r="${radius + ring / 2}" fill="#ffffff" stroke="rgba(0,0,0,0.28)" stroke-width="1"/>`,
    ...shapes,
    `<circle cx="${c}" cy="${c}" r="${inner}" fill="#ffffff"/>`,
    text,
    "</svg>",
  ].join("");
};

/** The map's numbers as spreadsheet rows: one row per collection site, located or not. */
export const mapDataRows = (model, trapSites) => [
  [
    "Collection site",
    "County",
    "Latitude",
    "Longitude",
    ...model.columns.map(entryLabel),
    "Total count",
  ],
  ...model.rows.map((row, r) => {
    const site = trapSites?.[row.key?.[0]];
    const position = location(site);
    return [
      entryLabel(row),
      site?.county ?? "",
      position ? position[0] : "",
      position ? position[1] : "",
      ...model.columns.map((_, c) => cellValue(model, r, c)),
      model.rowTotals[r] ?? 0,
    ];
  }),
];
